import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router";
import { ApiClientError, apiClient } from "../api/client.js";
import { useAuth } from "../auth/AuthProvider.js";

type AdminState =
  | { status: "loading" }
  | { status: "ready"; generations: Awaited<ReturnType<typeof apiClient.listAdminGenerationHistory>>; videos: Awaited<ReturnType<typeof apiClient.listAdminTrashedAssets>>["assets"]; events: Awaited<ReturnType<typeof apiClient.listAdminAuditEvents>>["items"] }
  | { status: "error"; message: string };

function time(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).format(new Date(value));
}

export function GenerationAuditPage() {
  const navigate = useNavigate();
  const { state: authState, recoverAuthentication } = useAuth();
  const [state, setState] = useState<AdminState>({ status: "loading" });
  const [restoring, setRestoring] = useState<string>();
  const [detail, setDetail] = useState<Awaited<ReturnType<typeof apiClient.getAdminGeneration>>>();
  const [loadingDetail, setLoadingDetail] = useState<string>();

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const [generations, deleted, audit] = await Promise.all([
        apiClient.listAdminGenerationHistory({ limit: 50 }),
        apiClient.listAdminTrashedAssets({ kind: "video", limit: 100 }),
        apiClient.listAdminAuditEvents({ targetType: "asset", limit: 100 })
      ]);
      setState({ status: "ready", generations, videos: deleted.assets.filter((asset) => asset.origin === "seedance_generated"), events: audit.items });
    } catch (error) {
      if (recoverAuthentication(error)) return;
      setState({ status: "error", message: error instanceof ApiClientError ? `加载审计失败（请求 ${error.requestId}）。` : "网络连接不可用，请稍后重试。" });
    }
  }, [recoverAuthentication]);

  useEffect(() => { void load(); }, [load]);
  if (authState.status !== "authenticated") return null;
  if (authState.user.role !== "system_admin") return <Navigate to="/projects" replace />;

  async function restore(assetId: string) {
    setRestoring(assetId);
    try {
      await apiClient.restoreAssetAsAdministrator(assetId);
      await load();
    } finally {
      setRestoring(undefined);
    }
  }

  async function viewDetail(jobId: string) {
    setLoadingDetail(jobId);
    try {
      setDetail(await apiClient.getAdminGeneration(jobId));
    } finally {
      setLoadingDetail(undefined);
    }
  }

  return (
    <main className="foundation-placeholder generation-history-page">
      <header className="foundation-placeholder__header"><div><p className="foundation-eyebrow">系统管理员</p><h1>生成审计与恢复</h1></div><button className="foundation-button foundation-button--ghost" type="button" onClick={() => navigate("/projects")}>返回项目</button></header>
      <section className="generation-history-page__content" aria-label="生成审计内容">
        {state.status === "loading" ? <div className="foundation-state-card"><span className="foundation-spinner" aria-hidden="true" /><strong>正在加载审计记录…</strong></div> : null}
        {state.status === "error" ? <div className="foundation-state-card" role="alert"><strong>{state.message}</strong><button className="foundation-button foundation-button--ghost" type="button" onClick={() => void load()}>重试</button></div> : null}
        {state.status === "ready" ? <>
          <section className="generation-history-section"><h2>最近生成</h2><div className="generation-history-table-wrap"><table className="generation-history-table"><thead><tr><th>提交人</th><th>项目</th><th>状态</th><th>Token</th><th>审计</th></tr></thead><tbody>{state.generations.items.map((item) => <tr key={item.id}><td>{item.ownerDisplayName ?? item.ownerId}</td><td>{item.projectName}</td><td>{item.state}</td><td>{item.usage.providerReported ? item.usage.totalTokens ?? "0" : "上游未提供"}</td><td><button className="foundation-button foundation-button--ghost" type="button" disabled={loadingDetail === item.id} onClick={() => void viewDetail(item.id)}>{loadingDetail === item.id ? "加载中…" : "查看完整详情"}</button></td></tr>)}</tbody></table></div>{detail ? <section className="generation-audit-detail" aria-label="生成完整审计详情"><header><h3>生成完整审计详情</h3><button className="foundation-button foundation-button--ghost" type="button" onClick={() => setDetail(undefined)}>关闭</button></header><dl><div><dt>状态</dt><dd>{detail.state}</dd></div><div><dt>上游任务</dt><dd>{detail.upstreamTaskId ?? "尚未获得"}</dd></div><div><dt>模型</dt><dd>{typeof detail.snapshot.request === "object" && detail.snapshot.request !== null && "model" in detail.snapshot.request ? String(detail.snapshot.request.model) : "未记录"}</dd></div></dl><h4>完整提示词</h4><pre>{typeof detail.snapshot.finalPrompt === "string" ? detail.snapshot.finalPrompt : "未记录"}</pre><h4>编译请求与引用</h4><pre>{JSON.stringify({ request: detail.snapshot.request, references: detail.snapshot.references }, null, 2)}</pre></section> : null}</section>
          <section className="generation-history-section"><h2>已删除的生成视频</h2>{state.videos.length === 0 ? <p className="foundation-muted">没有可恢复的生成视频。</p> : <ul className="generation-recovery-list">{state.videos.map((asset) => <li key={asset.id}><span><strong>{asset.displayName}</strong><small>原作者：{asset.ownerDisplayName ?? "未知"} · 删除于 {asset.trashedAt ? time(asset.trashedAt) : "未知"}</small></span><button className="foundation-button foundation-button--primary" type="button" disabled={restoring === asset.id} onClick={() => void restore(asset.id)}>{restoring === asset.id ? "正在恢复…" : "恢复视频"}</button></li>)}</ul>}</section>
          <section className="generation-history-section"><h2>素材操作审计</h2><ul className="generation-audit-list">{state.events.map((event) => <li key={event.id}><strong>{event.eventType}</strong><span>{event.ownerDisplayName ?? "系统"} · {time(event.createdAt)}</span></li>)}</ul></section>
        </> : null}
      </section>
    </main>
  );
}
